export type AnswerMatchResult = 'exact' | 'soft' | 'miss'

export const MIN_SOFT_LENGTH = 4
export const MAX_SOFT_DISTANCE = 1

const ACCEPTED_SPLIT = /[;\n|]+/

export function normalizeAnswer(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/\s+/g, ' ')
}

export function expandAccepted(primary: string, extras?: string[] | null): string[] {
  const out = new Set<string>()
  const add = (raw: string | undefined | null) => {
    if (!raw?.trim()) return
    const trimmed = raw.trim()
    out.add(trimmed)
    for (const part of trimmed.split(ACCEPTED_SPLIT)) {
      const p = part.trim()
      if (p) out.add(p)
    }
  }
  add(primary)
  extras?.forEach(add)
  return [...out]
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (Math.abs(m - n) > MAX_SOFT_DISTANCE) {
    return MAX_SOFT_DISTANCE + 1
  }
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  let cur = new Array<number>(n + 1)
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    const ca = a.charCodeAt(i - 1)
    for (let j = 1; j <= n; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1
      cur[j] = Math.min(cur[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost)
    }
    ;[prev, cur] = [cur, prev]
  }
  return prev[n]!
}

export function matchAnswer(
  userAnswer: string,
  acceptedAnswers: string[],
): AnswerMatchResult {
  const user = normalizeAnswer(userAnswer)
  if (!user || acceptedAnswers.length === 0) {
    return 'miss'
  }
  const normalized = acceptedAnswers
    .map(normalizeAnswer)
    .filter(Boolean)
  if (normalized.some((a) => a === user)) {
    return 'exact'
  }
  if (user.length < MIN_SOFT_LENGTH) {
    return 'miss'
  }
  for (const accepted of normalized) {
    if (accepted.length < MIN_SOFT_LENGTH) continue
    if (levenshtein(user, accepted) <= MAX_SOFT_DISTANCE) {
      return 'soft'
    }
  }
  return 'miss'
}

export function matchPrimary(userAnswer: string, primary: string, extras?: string[] | null): AnswerMatchResult {
  return matchAnswer(userAnswer, expandAccepted(primary, extras))
}

export function isAccepted(result: AnswerMatchResult): boolean {
  return result === 'exact' || result === 'soft'
}
